import { api } from "_shared/_stores/api";
import IndividualSchema, { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import { buildUpdateIndividualProfilePayload } from "marketplace/individuals/profiles/_schemas/update-individual-profile-request-schema";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import z from "zod";

export const individualsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getIndividualById: build.query<IndividualJSON, { individualId: string }>({
      query: ({ individualId }) => `marketplace/individuals/${individualId}`,
      rawResponseSchema: IndividualSchema.IndividualJSONZ,
      providesTags: (result) =>
        result ? [{ type: "Individual" as const, id: result.id }] : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualsByUserIds: build.query<IndividualJSON[], { userIds: Set<string> }>({
      query: ({ userIds }) => ({
        url: "marketplace/individuals-by-userids",
        method: "POST",
        body: Array.from(userIds),
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const normalized = [...new Set(...queryArgs.userIds)].sort((a, b) => a.localeCompare(b));
        return `${endpointName}(${normalized.join(",")})`;
      },
      rawResponseSchema: z.array(IndividualSchema.IndividualJSONZ),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Individual" as const, id: id })),
              { type: "Individual" as const, id: "LIST" },
            ]
          : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualVendors: build.query<IndividualJSON[], void>({
      query: () => "marketplace/individual-vendors",
      rawResponseSchema: z.array(IndividualSchema.IndividualJSONZ),
      providesTags: [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualsByIds: build.query<IndividualJSON[], { ids: string[] }>({
      query: ({ ids }) => ({
        url: "marketplace/individuals-by-ids",
        method: "POST",
        body: ids,
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: z.array(IndividualSchema.IndividualJSONZ),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Individual" as const, id: id })),
              { type: "Individual" as const, id: "LIST" },
            ]
          : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualAvatar: build.query<string, { individualId: string }>({
      query: ({ individualId }) => `marketplace/individuals/${individualId}/avatar`,
      providesTags: (_result, _error, { individualId }) => [{ type: "Individual" as const, id: individualId }],
    }),
    downloadIndividualResume: build.query<{ blob: Blob; filename?: string }, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/resume`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition");
          const matches = contentDisposition
            ? new RegExp(/(?<=filename(?:=|\*=(?:[\w-]+'')))["|']?(?<filename>[^"';\n]+)["|']?/).exec(contentDisposition)
            : null;
          return { blob, filename: matches?.groups?.filename };
        },
      }),
    }),
    // #endregion

    // #region Mutations
    updateIndividual: build.mutation<
      IndividualJSON,
      { individualId: string; isVisible: boolean; originalProfile?: IndividualProfile; updatedProfile?: IndividualProfile }
    >({
      query: ({ individualId, isVisible, originalProfile, updatedProfile }) => ({
        url: `marketplace/individuals/${individualId}`,
        method: "PATCH",
        body: buildUpdateIndividualProfilePayload(isVisible, originalProfile, updatedProfile),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: IndividualSchema.IndividualJSONZ,
      invalidatesTags: (_result, _error, { individualId }) => [{ type: "Individual" as const, id: individualId }],
    }),
    createIndividualAvatar: build.mutation<void, { individualId: string; file: File }>({
      query: ({ individualId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `marketplace/individuals/${individualId}/avatar`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { individualId }) => [{ type: "Individual" as const, id: individualId }],
    }),
    createIndividualResume: build.mutation<void, { individualId: string; file: File }>({
      query: ({ individualId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `marketplace/individuals/${individualId}/resume`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { individualId }) => [{ type: "Individual" as const, id: individualId }],
    }),
    deleteIndividualAvatar: build.mutation<void, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/avatar`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { individualId }) => [{ type: "Individual" as const, id: individualId }],
    }),
    deleteIndividualResume: build.mutation<void, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/resume`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { individualId }) => [{ type: "Individual" as const, id: individualId }],
    }),
    contactIndividual: build.mutation<ForumJSON, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/contact`,
        method: "POST",
        body: {},
      }),
    }),
    // #endregion
  }),
});

export const {
  useGetIndividualByIdQuery,
  useGetIndividualsByUserIdsQuery,
  useGetIndividualVendorsQuery,
  useGetIndividualsByIdsQuery,
  useGetIndividualAvatarQuery,
  useDownloadIndividualResumeQuery,
  useUpdateIndividualMutation,
  useCreateIndividualAvatarMutation,
  useCreateIndividualResumeMutation,
  useDeleteIndividualAvatarMutation,
  useDeleteIndividualResumeMutation,
  useContactIndividualMutation,
} = individualsApi;
